/**
 * TypeScript types for critique editing feature
 * Feature: 006-critique-editing (crop)
 * Feature: 011-light-editing (light adjustments)
 */

/**
 * Crop edit data structure (stored in JSONB)
 * All coordinates are percentages (0-100) for resolution independence
 */
export interface CropData {
  x: number // Left offset (percentage 0-100)
  y: number // Top offset (percentage 0-100)
  width: number // Crop width (percentage 0-100)
  height: number // Crop height (percentage 0-100)
  aspectRatio: 'free' | '1:1' | '3:4' | '4:3' | '2:3' | '3:2' | '9:16' | '16:9' | '24:65' | '65:24' // Aspect ratio constraint (UI reference only)
  computedAspectRatio?: number // Actual computed aspect ratio (width/height) - stored for free crops to avoid recalculation issues
}

/**
 * Light edit data (new in 011-light-editing)
 * All values are perceptual ranges used in photo editing UIs
 */
export interface LightEditData {
  /** Exposure adjustment in EV stops (-2.0 to +2.0) */
  exposure: number

  /** Contrast adjustment (-1.0 to +1.0) */
  contrast: number

  /** Highlights adjustment (brightens/darkens bright areas, -100 to +100) */
  highlights: number

  /** Shadows adjustment (brightens/darkens dark areas, -100 to +100) */
  shadows: number

  /** Whites adjustment (shifts white point, -100 to +100) */
  whites: number

  /** Blacks adjustment (shifts black point, -100 to +100) */
  blacks: number
}

/**
 * Combined edit data (crop + light)
 * Allows critics to suggest both compositional and tonal improvements
 * Note: At least one of crop or light must be present
 */
export interface CombinedEditData {
  crop?: CropData
  light?: LightEditData
}

/**
 * Parsed edit from markdown block
 * Feature: 069-markdown-edits-migration
 */
export interface ParsedEdit {
  type: 'markup' | 'edit'
  data: unknown // MarkupEditData or CombinedEditData
}

/**
 * Result of parsing markdown for edit blocks
 * Feature: 069-markdown-edits-migration
 */
export interface EditBlockParseResult {
  /** Parsed edits (markup shapes and/or crop/light edits) */
  edits: ParsedEdit[]
  /** Original markdown with edit blocks removed */
  cleanedMarkdown: string
  /** Validation errors (does not prevent submission) */
  errors: string[]
}

/**
 * Default values for new light edits (neutral adjustments)
 */
export const DEFAULT_LIGHT_EDIT: LightEditData = {
  exposure: 0.0,
  contrast: 0.0,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
}

/**
 * Validation ranges for light edits
 */
export const LIGHT_EDIT_RANGES = {
  exposure: { min: -2.0, max: 2.0, step: 0.1 },
  contrast: { min: -1.0, max: 1.0, step: 0.1 },
  highlights: { min: -100, max: 100, step: 1 },
  shadows: { min: -100, max: 100, step: 1 },
  whites: { min: -100, max: 100, step: 1 },
  blacks: { min: -100, max: 100, step: 1 },
} as const

/**
 * Edit type discriminator
 */
export type CritiqueEditType = 'crop' | 'light' | 'combined' | 'markup'

/**
 * Polymorphic edit data union
 */
export type EditData = CropData | LightEditData | CombinedEditData

/**
 * Database record (snake_case from database)
 * Represents a row from the critique_edits table
 */
export interface CritiqueEditRow {
  id: string
  critique_id: string
  edit_type: CritiqueEditType
  edit_data: EditData // Type varies by edit_type
  base_edit_id: string | null
  created_at: string
  updated_at: string
}

/**
 * API response (camelCase for frontend)
 * Represents an edit suggestion for a critique
 */
export interface CritiqueEdit {
  id: string
  critiqueId: string
  editType: CritiqueEditType
  editData: EditData
  baseEditId: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Partial edit data (for previewing before submission)
 * Feature: 043-critique-edit-suggestions
 * Used when showing edit preview before the edit is saved to the database
 */
export interface PartialCritiqueEdit {
  editType: CritiqueEditType
  editData: EditData
}

/**
 * Union type for both full and partial edits
 */
export type CritiqueEditOrPartial = CritiqueEdit | PartialCritiqueEdit

/**
 * Type guards for edit data discrimination
 */
export function isCropEdit(edit: CritiqueEditOrPartial): edit is (CritiqueEdit | PartialCritiqueEdit) & { editData: CropData } {
  return edit.editType === 'crop'
}

export function isLightEdit(edit: CritiqueEditOrPartial): edit is (CritiqueEdit | PartialCritiqueEdit) & { editData: LightEditData } {
  return edit.editType === 'light'
}

export function isCombinedEdit(edit: CritiqueEditOrPartial): edit is (CritiqueEdit | PartialCritiqueEdit) & { editData: CombinedEditData } {
  return edit.editType === 'combined'
}

/**
 * API request body for creating a new edit
 */
export interface CreateEditRequest {
  editType: CritiqueEditType
  editData: EditData
  baseEditId?: string // Optional: parent edit for chaining
}

/**
 * API request body for updating an existing edit
 * Note: Cannot change editType or baseEditId after creation
 */
export interface UpdateEditRequest {
  editData: EditData
}

/**
 * Composed edit chain result
 * Represents the final crop after composing all edits in a chain
 */
export interface ComposedCrop {
  x: number // 0-100 (percentage of original image)
  y: number // 0-100 (percentage of original image)
  width: number // 0-100 (percentage of original image)
  height: number // 0-100 (percentage of original image)
}

/**
 * CSS styles for applying edits to images
 * Generated by editRenderer library
 */
export interface EditStyles {
  clipPath?: string // CSS clip-path for crop
  filter?: string // CSS filter for exposure/color (future)
  transform?: string // CSS transform (future: rotation)
  WebkitClipPath?: string // Browser compatibility for clip-path
}
